import {
  styled,
  TextField,
} from "@mui/material";
import React, {
  forwardRef,
  useEffect,
  useState,
} from "react";
import NumberFormat from "react-number-format";
import {
  colors,
  defaultStyle,
} from "theme/defaultStyle";
import theme from "theme/default";

interface IProps {
  hasError?: boolean;
  name: string;
  label?: string;
  type?: string;
  disabled?: boolean;
  allowNegative?: boolean;
  maxLength?: number;
  maxNumberOfDigits?: number;
  value: string;
  onBlur?: (value: string) => void;
  onChange: (e: any, val: number | string) => void;
  placeholder?: string;
  showOutsideError?: boolean;
  showZero ?: boolean;
}

const CssTextField = styled(TextField)((props) => ({
  "& input.MuiInputBase-input": theme.custom.textField.inputOutlined,
  "& input.MuiInputBase-input.Mui-disabled": {
    ...theme.custom.textField.inputOutlinedDisabled,
    color: props.error ? defaultStyle.control.errorColor : colors.grey13,
  },
  "& .MuiOutlinedInput-root.Mui-disabled": {
    backgroundColor: colors.grey10,
  },  
}));

const CurrencyFormatCustom = ({
  inputRef,
  getInputRef,
  onChange,
  name,
  decimalScale,
  maxNumberOfDigits = 29,
  placeholder, // for 19.2 (inlcuding thousands separator, decimal point, and $sign)
  showOutsideError =true,
  ...rest
}: any) => {
  return (
    <NumberFormat
      {...rest}
      getInputRef={getInputRef ?? inputRef}
      onValueChange={(values: any) => {
        onChange({
          target: {
            name: name,
            value: values.value,
          },
        });
      }}
      thousandSeparator=","
      decimalSeparator="."
      decimalScale={2}
      fixedDecimalScale
      isNumericString
      prefix="$"
      name={name}
      isAllowed={({ formattedValue }) => formattedValue.length <= maxNumberOfDigits}
      style={{ paddingRight: "10px" }}
      placeholder={placeholder}
    />
  );
};

const CurrencyInput = forwardRef<HTMLInputElement, IProps>(({
  hasError = false,
  name,
  label,
  type,
  allowNegative = true,
  maxLength,
  maxNumberOfDigits,
  disabled = false,
  value,
  onBlur,
  onChange,
  placeholder,
  showOutsideError,
  showZero = false,
}, ref) => {
  
  const [internalValue, setInternalValue] = useState(value);
  const [shrinkable, setShrinkable] = useState<boolean>(false);

  const handleOnBlur = (event: any) => {
    const newValue = event.target.value;
    if (newValue === "") {
      setShrinkable(false);
      return;
    }

    onBlur && onBlur(newValue);
  };

  const handleOnChange = (e: any) => {
    const newValue = e.target.value;
    setShrinkable(newValue !== "");
    setInternalValue(newValue);
    onChange && onChange(e, newValue);
  };

  useEffect(() => {
    setShrinkable(internalValue !== undefined && internalValue !== "");
  }, [internalValue]);

  useEffect(() => {
    // hack to clean the value after issuing a product
    if (disabled && Number(value) === 0 && !showZero) setInternalValue("");

    //hack to set correct value when revise a product
    if (Number(value) !== Number(internalValue)) setInternalValue(value);
  }, [disabled, internalValue, value]);

  return (
    <CssTextField
      data-testid={name}
      style={{
        borderColor: "#dc3545",
      }}
      error={hasError}
      value={internalValue}
      {...{ label, name, type, disabled }}
      onBlur={handleOnBlur}
      onFocus={() => setShrinkable(true)}
      fullWidth
      InputProps={{
        disableUnderline: true,
        inputComponent: CurrencyFormatCustom,
        inputProps: {
          allowNegative: allowNegative,
          maxLength: maxLength,
          maxNumberOfDigits: maxNumberOfDigits,
          "aria-errormessage": hasError && !showOutsideError ? "rhfvalidationerror" : "",
          getInputRef: ref,
        },
        sx: {
          '& .MuiInputBase-input::placeholder': {
            color: hasError && !showOutsideError ? colors.red01 : undefined,
            opacity: 1,
          },
        },
      }}
      variant="outlined"
      InputLabelProps={{
        shrink: hasError || shrinkable,
      }}
      onChange={handleOnChange}
      placeholder={placeholder}
    />
  );
});

CurrencyInput.displayName = "CurrencyInput";

export default CurrencyInput;
